/**
 * Pure helpers for the Incoming Stock "No PO yet" cards (deliveries that
 * arrived with no purchase order). Backend: /api/unmatched-arrivals.
 */

export const ARRIVAL_STORE_LABELS = { phaser: 'Phaser FPV', ccc: 'CCC', unknown: 'Store?' };

/** Tabs that show the cards. Archived / Website Team are past receiving. */
export const ARRIVAL_PANEL_TABS = ['all', 'draft', 'waiting_arrival', 'arrived'];

export function arrivalAgeHours(arrival, now = Date.now()) {
  const t = Date.parse(arrival?.createdAt || '');
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 3600000)) : null;
}

/** "Waiting 5h" / "Waiting 2d 3h", and the Arrived-tab age colour scale. */
export function arrivalAge(arrival, now = Date.now()) {
  const hours = arrivalAgeHours(arrival, now);
  if (hours == null) return { label: null, color: '#e8730c' };
  const label = hours < 24 ? `Waiting ${hours}h` : `Waiting ${Math.floor(hours / 24)}d ${hours % 24}h`;
  const color = hours < 24 ? '#1a874f' : hours < 48 ? '#e8730c' : '#d82c0d';
  return { label, color };
}

function poVendor(po) {
  return String(po?.vendor_display_name || po?.supplier_name || po?.vendor || po?.from || '').trim();
}

export function poLabel(po) {
  return String(po?.reference || po?.number || po?.id || '').trim();
}

function words(text) {
  return String(text || '').toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
}

/**
 * POs worth offering in the "Link to PO" picker: same store (when known),
 * not archived, vendor-name matches first, then newest.
 */
export function rankCandidatePos(arrival, purchaseOrders = [], limit = 30) {
  const wanted = new Set(words(arrival?.supplier));
  const store = arrival?.store && arrival.store !== 'unknown' ? arrival.store : null;
  const seen = new Set();
  return (Array.isArray(purchaseOrders) ? purchaseOrders : [])
    .filter((po) => {
      const id = String(po?.id || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      if (store && po.storeKey && po.storeKey !== store) return false;
      const status = po?.localMetadata?.status || po?.status;
      return status !== 'archived';
    })
    .map((po) => {
      const vendorWords = words(poVendor(po));
      const score = vendorWords.filter((w) => wanted.has(w)).length;
      return { po, score, created: Date.parse(po.created_at || po.createdAt || po.date || '') || 0 };
    })
    .sort((a, b) => (b.score - a.score) || (b.created - a.created))
    .slice(0, limit)
    .map(({ po, score }) => ({
      id: String(po.id),
      reference: poLabel(po),
      vendor: poVendor(po),
      storeKey: po.storeKey || null,
      match: score > 0
    }));
}

/** Free-text filter mirror of the PO search box. */
export function arrivalMatchesFilter(arrival, filter) {
  const term = String(filter || '').trim().toLowerCase();
  if (!term) return true;
  return [arrival?.supplier, arrival?.tracking, arrival?.carrier, arrival?.notes, arrival?.resolution?.poReference]
    .some((value) => value && String(value).toLowerCase().includes(term));
}

export function arrivalMatchesStore(arrival, storeFilter) {
  if (!storeFilter || storeFilter === 'all') return true;
  return arrival?.store === storeFilter || arrival?.store === 'unknown';
}

/**
 * Split visible arrivals into open cards (oldest first) and the "recently
 * linked" list (newest first). Rhys: once every card is linked or dismissed
 * the panel goes away, so closed ones only show while something is open.
 * The backend still returns closed ones for 14 days.
 */
export function arrivalPanelSections(arrivals = []) {
  const list = Array.isArray(arrivals) ? arrivals : [];
  const open = list.filter((a) => a.status === 'open')
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  if (!open.length) return { open, closed: [] };
  const closed = list.filter((a) => a.status !== 'open')
    .sort((a, b) => String(b.resolvedAt || '').localeCompare(String(a.resolvedAt || '')));
  return { open, closed };
}
